import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Download, Users, BookOpen, FileText, Loader2 } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AdminExportar() {
  const [loadingMatriculadosCSV, setLoadingMatriculadosCSV] = useState(false);
  const [loadingMatriculadosPDF, setLoadingMatriculadosPDF] = useState(false);
  const [loadingRecurseroCSV, setLoadingRecurseroCSV] = useState(false);
  const [loadingRecurseroPDF, setLoadingRecurseroPDF] = useState(false);

  const handleExportMatriculados = async (format) => {
    const setLoading = format === 'csv' ? setLoadingMatriculadosCSV : setLoadingMatriculadosPDF;
    setLoading(true);
    try {
      const endpoint = format === 'csv' 
        ? `${API_URL}/admin/export/matriculados` 
        : `${API_URL}/admin/export/matriculados/pdf`;
      
      const response = await axios.get(endpoint, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `matriculados.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Error exportando:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleExportRecursero = async (format) => {
    const setLoading = format === 'csv' ? setLoadingRecurseroCSV : setLoadingRecurseroPDF;
    setLoading(true);
    try {
      const endpoint = format === 'csv' 
        ? `${API_URL}/admin/export/recursero` 
        : `${API_URL}/admin/export/recursero/pdf`;
      
      const response = await axios.get(endpoint, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `recursero.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Error exportando:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Exportar Datos</h1>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Export Matriculados */}
        <Card className="card-hover">
          <CardHeader>
            <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mb-4">
              <Users className="w-6 h-6 text-primary" />
            </div>
            <CardTitle>Exportar Matriculados</CardTitle>
            <CardDescription>
              Descarga un archivo CSV con todos los datos de los trabajadores sociales matriculados.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-sm text-gray-500 mb-4">
              <p><strong>Incluye:</strong></p>
              <ul className="list-disc list-inside mt-1 space-y-1">
                <li>Datos personales (nombre, DNI, matrícula)</li>
                <li>Información de contacto</li>
                <li>Domicilios real y laboral</li>
                <li>Título profesional</li>
              </ul>
            </div>
            <div className="space-y-2">
              <Button 
                onClick={() => handleExportMatriculados('csv')} 
                className="w-full" 
                data-testid="export-matriculados-csv-btn"
                disabled={loadingMatriculadosCSV}
              >
                {loadingMatriculadosCSV ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                Exportar CSV
              </Button>
              <Button 
                onClick={() => handleExportMatriculados('pdf')} 
                variant="outline"
                className="w-full border-primary text-primary hover:bg-primary hover:text-white" 
                data-testid="export-matriculados-pdf-btn"
                disabled={loadingMatriculadosPDF}
              >
                {loadingMatriculadosPDF ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
                Exportar PDF
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Export Recursero */}
        <Card className="card-hover">
          <CardHeader>
            <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
              <BookOpen className="w-6 h-6 text-emerald-600" />
            </div>
            <CardTitle>Exportar Recursero</CardTitle>
            <CardDescription>
              Descarga un archivo CSV con los datos del recursero público (solo trabajadores visibles).
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-sm text-gray-500 mb-4">
              <p><strong>Incluye:</strong></p>
              <ul className="list-disc list-inside mt-1 space-y-1">
                <li>Datos del profesional</li>
                <li>Lugares de trabajo</li>
                <li>Áreas de trabajo</li>
                <li>Información institucional</li>
              </ul>
            </div>
            <div className="space-y-2">
              <Button 
                onClick={() => handleExportRecursero('csv')} 
                className="w-full bg-emerald-500 hover:bg-emerald-600" 
                data-testid="export-recursero-csv-btn"
                disabled={loadingRecurseroCSV}
              >
                {loadingRecurseroCSV ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                Exportar CSV
              </Button>
              <Button 
                onClick={() => handleExportRecursero('pdf')} 
                variant="outline"
                className="w-full border-emerald-500 text-emerald-600 hover:bg-emerald-500 hover:text-white" 
                data-testid="export-recursero-pdf-btn"
                disabled={loadingRecurseroPDF}
              >
                {loadingRecurseroPDF ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
                Exportar PDF
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
